import React, { useState, useEffect } from 'react';
import { Save, Loader2, Palette, Type, RotateCcw } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const FONT_OPTIONS = [
  'Playfair Display', 'Montserrat', 'Inter', 'Roboto', 'Open Sans', 'Lato',
  'Poppins', 'Raleway', 'Merriweather', 'Source Sans Pro', 'Nunito',
  'Work Sans', 'DM Sans', 'Outfit', 'Space Grotesk', 'Sora',
  'Crimson Text', 'Libre Baskerville', 'EB Garamond', 'Cormorant Garamond',
  'Josefin Sans', 'Barlow', 'Manrope', 'Plus Jakarta Sans'
];

const PRESET_PALETTES = [
  { name: 'Jock Tax Summit', primary: '#C5A059', secondary: '#14213D', accent: '#D4B06A' },
  { name: 'Ocean Blue', primary: '#0077B6', secondary: '#023E8A', accent: '#90E0EF' },
  { name: 'Forest Green', primary: '#059669', secondary: '#064E3B', accent: '#34D399' },
  { name: 'Royal Purple', primary: '#7C3AED', secondary: '#4C1D95', accent: '#A78BFA' },
  { name: 'Sunset Orange', primary: '#F97316', secondary: '#9A3412', accent: '#FBBF24' },
  { name: 'Rose Pink', primary: '#F43F5E', secondary: '#881337', accent: '#FDA4AF' },
  { name: 'Slate Gray', primary: '#64748B', secondary: '#1E293B', accent: '#94A3B8' },
  { name: 'Teal', primary: '#14B8A6', secondary: '#134E4A', accent: '#5EEAD4' },
];

const GlobalStylingTab = ({ credentials, onSuccess }) => {
  const [styling, setStyling] = useState({
    primary_color: '#C5A059',
    secondary_color: '#14213D',
    accent_color: '#D4B06A',
    heading_font: 'Playfair Display',
    body_font: 'Montserrat',
    border_radius: '0.75rem',
    font_scale: '1',
  });
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const getAuthHeader = () => {
    if (!credentials) return {};
    return { 'Authorization': 'Basic ' + btoa(`${credentials.username}:${credentials.password}`) };
  };

  useEffect(() => {
    const fetchStyling = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/site-config`);
        if (response.ok) {
          const data = await response.json();
          if (data.styling) setStyling(prev => ({ ...prev, ...data.styling }));
        }
      } catch (err) { console.error('Failed to fetch styling:', err); }
      finally { setIsLoading(false); }
    };
    fetchStyling();
  }, []);

  const saveStyling = async () => {
    setIsSaving(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/site-config`, {
        method: 'PUT',
        headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ styling })
      });
      if (response.ok) {
        onSuccess?.('Global styles saved! Refresh the site to see changes.');
      }
    } catch (err) { console.error('Failed to save styling:', err); }
    finally { setIsSaving(false); }
  };

  const applyPreset = (preset) => {
    setStyling(prev => ({
      ...prev,
      primary_color: preset.primary,
      secondary_color: preset.secondary,
      accent_color: preset.accent,
    }));
  };

  const resetDefaults = () => {
    setStyling({
      primary_color: '#C5A059', secondary_color: '#14213D', accent_color: '#D4B06A',
      heading_font: 'Playfair Display', body_font: 'Montserrat', border_radius: '0.75rem', font_scale: '1',
    });
  };

  if (isLoading) {
    return <div className="flex items-center justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  }

  return (
    <div className="space-y-8" data-testid="global-styling-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-xl font-semibold flex items-center gap-2"><Palette className="w-5 h-5 text-primary" /> Global Styling</h3>
          <p className="text-sm text-muted-foreground mt-1">Control fonts, colors, and styling across the entire site</p>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={resetDefaults} className="flex items-center gap-2 px-4 py-2 glass-card rounded-xl text-sm font-medium hover:bg-white/10 transition-colors" data-testid="reset-styling-btn">
            <RotateCcw className="w-4 h-4" /> Reset Defaults
          </button>
          <button onClick={saveStyling} disabled={isSaving} className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-xl font-medium transition-all hover:-translate-y-0.5 disabled:opacity-50" data-testid="save-styling-btn">
            {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save Styles
          </button>
        </div>
      </div>

      {/* Color Presets */}
      <div className="glass-card rounded-xl p-6">
        <h4 className="font-semibold mb-4">Color Presets</h4>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {PRESET_PALETTES.map((preset) => (
            <button key={preset.name} onClick={() => applyPreset(preset)}
              className="p-3 glass-card rounded-xl hover:bg-white/10 transition-all text-left group" data-testid={`preset-${preset.name.toLowerCase().replace(/\s+/g, '-')}`}>
              <div className="flex gap-1.5 mb-2">
                <div className="w-6 h-6 rounded-full border border-border" style={{ backgroundColor: preset.primary }} />
                <div className="w-6 h-6 rounded-full border border-border" style={{ backgroundColor: preset.secondary }} />
                <div className="w-6 h-6 rounded-full border border-border" style={{ backgroundColor: preset.accent }} />
              </div>
              <span className="text-xs font-medium">{preset.name}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Custom Colors */}
      <div className="glass-card rounded-xl p-6">
        <h4 className="font-semibold mb-4 flex items-center gap-2"><Palette className="w-4 h-4" /> Custom Colors</h4>
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { key: 'primary_color', label: 'Primary Color', desc: 'Buttons, accents, highlights' },
            { key: 'secondary_color', label: 'Secondary Color', desc: 'Secondary elements, labels' },
            { key: 'accent_color', label: 'Accent Color', desc: 'Hover states, subtle highlights' },
          ].map(({ key, label, desc }) => (
            <div key={key}>
              <label className="block text-sm font-medium mb-2">{label}</label>
              <div className="flex items-center gap-3">
                <input type="color" value={styling[key]} onChange={(e) => setStyling(p => ({ ...p, [key]: e.target.value }))}
                  className="w-12 h-12 rounded-lg cursor-pointer border border-border" data-testid={`color-${key}`} />
                <div>
                  <input type="text" value={styling[key]} onChange={(e) => setStyling(p => ({ ...p, [key]: e.target.value }))}
                    className="w-28 px-3 py-2 rounded-lg bg-background border border-border text-sm font-mono focus:border-primary focus:outline-none" />
                  <p className="text-xs text-muted-foreground mt-1">{desc}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
        {/* Live Preview */}
        <div className="mt-6 p-4 rounded-xl border border-border" style={{ background: `linear-gradient(135deg, ${styling.secondary_color}22, ${styling.primary_color}22)` }}>
          <p className="text-sm text-muted-foreground mb-2">Live Preview</p>
          <div className="flex items-center gap-3">
            <button className="px-4 py-2 rounded-full text-sm font-semibold text-white" style={{ backgroundColor: styling.primary_color }}>Primary Button</button>
            <button className="px-4 py-2 rounded-full text-sm font-semibold text-white" style={{ backgroundColor: styling.secondary_color }}>Secondary</button>
            <span className="px-3 py-1 rounded-full text-xs font-medium" style={{ backgroundColor: styling.accent_color + '33', color: styling.accent_color }}>Accent Tag</span>
          </div>
        </div>
      </div>

      {/* Typography */}
      <div className="glass-card rounded-xl p-6">
        <h4 className="font-semibold mb-4 flex items-center gap-2"><Type className="w-4 h-4" /> Typography</h4>
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium mb-2">Heading Font</label>
            <select value={styling.heading_font} onChange={(e) => setStyling(p => ({ ...p, heading_font: e.target.value }))}
              className="w-full px-4 py-3 rounded-xl bg-background border border-border focus:border-primary focus:outline-none" data-testid="heading-font-select">
              {FONT_OPTIONS.map(f => <option key={f} value={f}>{f}</option>)}
            </select>
            <p className="text-sm text-muted-foreground mt-2" style={{ fontFamily: `'${styling.heading_font}', serif` }}>
              The quick brown fox jumps over the lazy dog
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Body Font</label>
            <select value={styling.body_font} onChange={(e) => setStyling(p => ({ ...p, body_font: e.target.value }))}
              className="w-full px-4 py-3 rounded-xl bg-background border border-border focus:border-primary focus:outline-none" data-testid="body-font-select">
              {FONT_OPTIONS.map(f => <option key={f} value={f}>{f}</option>)}
            </select>
            <p className="text-sm text-muted-foreground mt-2" style={{ fontFamily: `'${styling.body_font}', sans-serif` }}>
              The quick brown fox jumps over the lazy dog
            </p>
          </div>
        </div>
      </div>

      {/* Layout */}
      <div className="glass-card rounded-xl p-6">
        <h4 className="font-semibold mb-4">Layout & Spacing</h4>
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium mb-2">Border Radius</label>
            <select value={styling.border_radius} onChange={(e) => setStyling(p => ({ ...p, border_radius: e.target.value }))}
              className="w-full px-4 py-3 rounded-xl bg-background border border-border focus:border-primary focus:outline-none" data-testid="border-radius-select">
              <option value="0">Sharp (0)</option>
              <option value="0.375rem">Slight (0.375rem)</option>
              <option value="0.5rem">Small (0.5rem)</option>
              <option value="0.75rem">Medium (0.75rem)</option>
              <option value="1rem">Large (1rem)</option>
              <option value="1.5rem">Extra Large (1.5rem)</option>
              <option value="9999px">Pill (full)</option>
            </select>
            <div className="flex gap-3 mt-3">
              {['0', '0.5rem', '0.75rem', '1.5rem', '9999px'].map(r => (
                <div key={r} className="w-12 h-12 border-2 border-primary/50 bg-primary/10" style={{ borderRadius: r }} />
              ))}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Font Scale</label>
            <select value={styling.font_scale} onChange={(e) => setStyling(p => ({ ...p, font_scale: e.target.value }))}
              className="w-full px-4 py-3 rounded-xl bg-background border border-border focus:border-primary focus:outline-none" data-testid="font-scale-select">
              <option value="0.875">Compact (87.5%)</option>
              <option value="1">Default (100%)</option>
              <option value="1.125">Large (112.5%)</option>
              <option value="1.25">Extra Large (125%)</option>
            </select>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GlobalStylingTab;
